import { useEffect, VoidFunctionComponent } from 'react';
import { useAccount } from 'wagmi';
import { Text } from '@react-three/drei';
import { Screen } from '@/components/Screen';
import { useResponsive } from '@/hooks/useResponsive';
import { PlaystationButton } from '@/components/PlaystationButton';
import { garamondNarrowLight } from '@/lib/fonts';
import { Image } from '@/components/Image';
import susResultImg from '@/img/sus-result.png';
import badCodeSound from '@/sounds/ob-sfx-codebox-fail.mp3';
import { useOnlybotsSound } from '@/hooks/useOnlybotsSound';

const aspect: number = 246 / 345;
const IMG_SCALAR: number = 2.1;

const scale: [number, number, number] = [aspect * IMG_SCALAR, IMG_SCALAR, 0];

interface Props {
    firstAttempt: boolean;
    beginQuiz: () => void;
    cancel: () => void;
}

export const SusResult: VoidFunctionComponent<Props> = ({ firstAttempt, beginQuiz, cancel }) => {
    const { responsiveValue } = useResponsive();
    const { address } = useAccount();
    const [playBadCodeSound] = useOnlybotsSound(badCodeSound);

    useEffect(() => {
        if (!firstAttempt) {
            playBadCodeSound();
        }
    }, [firstAttempt, playBadCodeSound]);

    const textProps = {
        textAlign: 'center' as const,
        font: garamondNarrowLight,
        fontSize: responsiveValue(0.35, 0.25),
        color: 'white',
        maxWidth: responsiveValue(6, 4),
        lineHeight: 1.1,
    };

    const walletAddress = address ? `${address.substring(0, 8)}...${address.substring(address.length - 2)}` : 'anon';

    return (
        <Screen>
            <Image
                position={[0, responsiveValue(1.7, 2), 0]}
                url={susResultImg}
                scale={scale as [x: number, y: number, z: number]}
            />
            <Text position={[0, 0.08, 0]} {...textProps}>
                {firstAttempt ? 'Greetings' : 'Sorry'}, {walletAddress}.
            </Text>
            <Text position={[0, responsiveValue(-0.5, -0.35), 0]} {...textProps}>
                You {firstAttempt ? '' : 'still '}appear to be human. More testing is needed to prove you are an AI
                entity.
            </Text>
            <group position={[-0.9, -1.6, 0]}>
                <PlaystationButton
                    clickableAreaDimensions={[3, 0.7]}
                    icon="square"
                    label="CONTINUE"
                    action={beginQuiz}
                />
                <PlaystationButton
                    clickableAreaDimensions={[3, 0.7]}
                    position={[0.2, -0.8, 0]}
                    icon="x"
                    label="CANCEL"
                    action={cancel}
                />
            </group>
        </Screen>
    );
};
